import { FC, ReactElement, Ref, forwardRef } from 'react';
import { Breakpoint, Dialog, Slide } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';

import './index.scss';
import Text from '../Text';
import { DrawerWrapper } from '../../../components/Menu/styledComponents';
import { useScreenWidth } from '../../Helpers/hooks';
import { PRIMARY } from '../../Helpers/colors';
import { Close, InfoFilled } from '../../Assets/svgIcons';
import CustomButton from '../Button';
import { ProgressIndicator } from '../../../components/ProgressIndicator';

interface DialogProperties {
	Content: FC<{ onClose: () => void }>;
	Icon?: FC<{ color?: string }>;
	contentHeight?: string;
	iconBackgroundColor?: string;
	isBusy?: boolean;
	isSecondary?: boolean;
	maxWidth?: Breakpoint;
	noPadding?: boolean;
	onClose: () => void;
	open: boolean;
	title: string;
	titleOnTop?: boolean;
}

const Transition = forwardRef(
	(
		props: TransitionProps & {
			children: ReactElement<any, any>;
		},
		ref: Ref<unknown>,
	) => <Slide direction="up" ref={ref} {...props} />,
);

Transition.displayName = 'Transition';

const CustomAlert: FC<DialogProperties> = ({
	open,
	isBusy,
	onClose,
	title,
	Content,
	maxWidth = 'sm',
	contentHeight = '100%',
	titleOnTop = true,
	Icon = InfoFilled,
	iconBackgroundColor = PRIMARY.TERTIARY,
	isSecondary = false,
	noPadding = false,
}) => {
	const screenWidth = useScreenWidth();
	const isMobile = screenWidth <= 500;

	return isMobile ? (
		<DrawerWrapper
			variant="temporary"
			anchor="bottom"
			open={open}
			PaperProps={{
				style: {
					borderRadius: '16px 16px 0 0',
					width: '100%',
					overflow: 'auto',
				},
			}}
		>
			<div className="drawerHeader">
				{Boolean(title) && <Text title={title} type="h6" fontWeight="semiBold" />}
				<CustomButton onClick={onClose} iconOnly={<Close />} buttonType="quaternary" />
			</div>
			<div className={`drawerContent ${noPadding ? 'noPadding' : ''}`}>
				<Content onClose={onClose} />
			</div>
		</DrawerWrapper>
	) : (
		<Dialog
			TransitionComponent={Transition}
			open={open}
			fullWidth
			onClose={onClose}
			maxWidth={maxWidth}
			sx={{
				'.MuiPaper-root': {
					borderRadius: '13px',
					boxShadow: `0px 24px 32px -8px rgba(0, 0, 0, 0.08), 0px 8px 12px -8px rgba(0, 0, 0, 0.08)`,
				},
				'.MuiBackdrop-root': {
					background: `rgba(30, 29, 29, 0.56)`,
					backdropFilter: `blur(4px)`,
				},
			}}
		>
			<div className={`modalContainer ${isSecondary ? 'secondary' : ''}`}>
				<div className="contentWrapper">
					{isBusy && <ProgressIndicator />}
					<div className="headerWrapper">
						<div className="icon" style={{ backgroundColor: iconBackgroundColor }}>
							<Icon color={PRIMARY.BASE} />
						</div>
						{titleOnTop && <Text title={title} type="plg" fontWeight="semiBold" className="marginRight32" />}
					</div>
					{open && (
						<div className="content" style={{ height: contentHeight }}>
							{!titleOnTop && <Text title={title} type="plg" fontWeight="semiBold" />}
							<Content onClose={onClose} />
						</div>
					)}
					<CustomButton className="closeAction" onClick={onClose} iconOnly={<Close />} buttonType="quaternary" />
				</div>
			</div>
		</Dialog>
	);
};

export default CustomAlert;
